<template>
  <div class="pr-view">
    <!-- Pedir el enlace -->
    <form v-if="mode === 'forgot' && !sent" class="pr-card" @submit.prevent="onForgot">
      <div class="pr-card__logo"><ParkosMark :size="30" /><span class="pr-card__logo-text">Parkos</span></div>
      <h1 class="pr-card__title">¿Olvidaste tu contraseña?</h1>
      <p class="pr-card__subtitle">Escribe el correo con el que entras y te enviaremos un enlace para crear una nueva.</p>

      <label class="pr-card__label" for="pr-email">Correo</label>
      <input id="pr-email" v-model.trim="email" type="email" class="pr-card__input" autocomplete="username" required />

      <p v-if="error" class="pr-card__error">{{ error }}</p>
      <button type="submit" class="pr-card__submit" :disabled="busy">{{ busy ? 'Enviando...' : 'Enviar enlace' }}</button>
      <router-link class="pr-card__link" to="/login">Volver a iniciar sesión</router-link>
    </form>

    <div v-else-if="mode === 'forgot'" class="pr-card pr-card--center">
      <span class="mdi mdi-email-check-outline pr-card__icon"></span>
      <h1 class="pr-card__title">Revisa tu correo</h1>
      <p class="pr-card__subtitle">Si <strong>{{ email }}</strong> corresponde a una cuenta activa, en unos minutos te llegará un enlace para crear una contraseña nueva. Vence en 1 hora.</p>
      <p class="pr-card__subtitle">¿No lo ves? Revisa la carpeta de spam.</p>
      <router-link class="pr-card__link" to="/login">Volver a iniciar sesión</router-link>
    </div>

    <!-- Crear la contraseña nueva -->
    <div v-else-if="loading" class="pr-card pr-card--center">
      <span class="mdi mdi-loading pr-card__spinner"></span>
      <p class="pr-card__subtitle">Verificando enlace...</p>
    </div>

    <div v-else-if="linkError" class="pr-card pr-card--center">
      <span class="mdi mdi-alert-circle-outline pr-card__icon pr-card__icon--bad"></span>
      <h1 class="pr-card__title">{{ linkError.title }}</h1>
      <p class="pr-card__subtitle">{{ linkError.text }}</p>
      <router-link class="pr-card__submit pr-card__submit--link" to="/olvide-contrasena">Pedir un enlace nuevo</router-link>
      <router-link class="pr-card__link" to="/login">Volver a iniciar sesión</router-link>
    </div>

    <div v-else-if="done" class="pr-card pr-card--center">
      <span class="mdi mdi-check-circle-outline pr-card__icon pr-card__icon--ok"></span>
      <h1 class="pr-card__title">Contraseña actualizada</h1>
      <p class="pr-card__subtitle">Por seguridad cerramos tus sesiones abiertas. Entra con tu contraseña nueva.</p>
      <router-link class="pr-card__submit pr-card__submit--link" to="/login">Iniciar sesión</router-link>
    </div>

    <form v-else class="pr-card" @submit.prevent="onReset">
      <div class="pr-card__logo"><ParkosMark :size="30" /><span class="pr-card__logo-text">Parkos</span></div>
      <h1 class="pr-card__title">Crea tu contraseña nueva</h1>
      <p class="pr-card__subtitle">{{ info.email }}</p>

      <label class="pr-card__label" for="pr-pass">Contraseña nueva</label>
      <input id="pr-pass" v-model="password" type="password" class="pr-card__input" autocomplete="new-password" minlength="8" required />
      <label class="pr-card__label" for="pr-pass2">Confirmar contraseña</label>
      <input id="pr-pass2" v-model="password2" type="password" class="pr-card__input" autocomplete="new-password" minlength="8" required />
      <p class="pr-card__hint">Mínimo 8 caracteres.</p>

      <p v-if="error" class="pr-card__error">{{ error }}</p>
      <button type="submit" class="pr-card__submit" :disabled="busy">{{ busy ? 'Guardando...' : 'Guardar contraseña' }}</button>
    </form>
  </div>
</template>

<script setup>
import ParkosMark from '@/components/Global/ParkosMark.vue'
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { requestPasswordReset, getPasswordResetInfo, resetPassword } from '@/services/auth'

const route = useRoute()
const mode = computed(() => (route.name === 'OlvideContrasena' ? 'forgot' : 'reset'))
const token = String(route.query.token || '')

const email = ref(typeof route.query.email === 'string' ? route.query.email : '')
const sent = ref(false)
const busy = ref(false)
const error = ref('')

const loading = ref(mode.value === 'reset')
const info = ref(null)
const linkError = ref(null)
const password = ref('')
const password2 = ref('')
const done = ref(false)

const LINK_ERRORS = {
  INVALID: { title: 'Enlace no válido', text: 'Este enlace no existe o ya fue reemplazado por uno más reciente.' },
  USED: { title: 'Enlace ya utilizado', text: 'Este enlace ya se usó para cambiar la contraseña.' },
  EXPIRED: { title: 'Enlace vencido', text: 'Este enlace ya venció. Pide uno nuevo para continuar.' },
}

onMounted(async () => {
  if (mode.value !== 'reset') return
  try {
    const result = await getPasswordResetInfo(token)
    if (result.valid) info.value = result
    else linkError.value = LINK_ERRORS[result.error] || LINK_ERRORS.INVALID
  } catch {
    linkError.value = { title: 'No pudimos verificar el enlace', text: 'Revisa tu conexión e intenta de nuevo.' }
  } finally {
    loading.value = false
  }
})

async function onForgot() {
  busy.value = true
  error.value = ''
  try {
    await requestPasswordReset(email.value)
    sent.value = true
  } catch (err) {
    error.value = err.message
  } finally {
    busy.value = false
  }
}

async function onReset() {
  error.value = ''
  if (password.value.length < 8) return (error.value = 'La contraseña debe tener al menos 8 caracteres.')
  if (password.value !== password2.value) return (error.value = 'Las contraseñas no coinciden.')
  busy.value = true
  try {
    await resetPassword(token, password.value)
    done.value = true
  } catch (err) {
    if (LINK_ERRORS[err.message]) linkError.value = LINK_ERRORS[err.message]
    else if (err.message === 'PASSWORD_TOO_SHORT') error.value = 'La contraseña debe tener al menos 8 caracteres.'
    else error.value = 'No se pudo cambiar la contraseña. Intenta de nuevo.'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.pr-view { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #e6eaf3; padding: 1rem; }
.pr-card { width: 100%; max-width: 380px; background: #ffffff; border-radius: 12px; box-shadow: 0 10px 30px rgba(30, 58, 95, 0.12); padding: 2rem 1.75rem; display: flex; flex-direction: column; }
.pr-card--center { align-items: center; text-align: center; }
.pr-card__logo { display: flex; align-items: center; gap: 0.5rem; color: #1e3a5f; margin-bottom: 1.5rem; }
.pr-card__logo .mdi { font-size: 1.4rem; }
.pr-card__logo-text { font-family: var(--font-display); font-size: 1.25rem; }
.pr-card__title { font-family: var(--font-display); font-size: 1.3rem; color: #1e3a5f; margin: 0 0 0.25rem; }
.pr-card__subtitle { font-family: var(--font-body); font-size: 0.85rem; color: #64748b; margin: 0 0 1.25rem; line-height: 1.5; }
.pr-card__label { font-family: var(--font-body); font-size: 0.8rem; color: #64748b; margin-bottom: 0.3rem; }
.pr-card__input { font-family: var(--font-body); font-size: 0.9rem; color: #1e3a5f; border: 1px solid var(--border-active); border-radius: 8px; padding: 0.6rem 0.75rem; margin-bottom: 1rem; }
.pr-card__input:focus { outline: none; border-color: #3b82f6; }
.pr-card__hint { font-family: var(--font-body); font-size: 0.75rem; color: #94a3b8; margin: -0.5rem 0 1rem; }
.pr-card__error { font-family: var(--font-body); font-size: 0.8rem; color: #e84545; margin: -0.5rem 0 1rem; }
.pr-card__submit { display: flex; align-items: center; justify-content: center; background: #1e3a5f; color: #ffffff; border: none; border-radius: 8px; padding: 0.7rem; font-family: var(--font-body); font-size: 0.9rem; font-weight: 500; cursor: pointer; text-decoration: none; width: 100%; box-sizing: border-box; }
.pr-card__submit:hover:not(:disabled) { background: #163049; }
.pr-card__submit:disabled { opacity: 0.7; cursor: default; }
.pr-card__submit--link { margin-bottom: 0.75rem; }
.pr-card__link { margin-top: 0.9rem; text-align: center; font-family: var(--font-body); font-size: 0.8rem; color: #3b82f6; text-decoration: none; }
.pr-card__icon { font-size: 2.6rem; color: #3b82f6; margin-bottom: 0.5rem; }
.pr-card__icon--bad { color: #e84545; }
.pr-card__icon--ok { color: #10b981; }
.pr-card__spinner { font-size: 1.6rem; color: #3b82f6; animation: pr-spin 0.8s linear infinite; }
@keyframes pr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>